
import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useAuth } from "../../AuthContext";
import { tokenIsExpired } from "../../authUtils";
import { Assessment, Receipt, Inventory, Settings } from "@mui/icons-material";
import ArrowCircleRightIcon from '@mui/icons-material/ArrowCircleRight';
import Diversity1Icon from '@mui/icons-material/Diversity1';
import { Card, CardContent, Button, Grid, Typography, Avatar, Box, IconButton } from "@mui/material";
import { fetchData, updateData, postData, deleteData } from '../../apiService';
import PaymentIcon from '@mui/icons-material/Payment';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import SummarizeIcon from '@mui/icons-material/Summarize';
import SupervisedUserCircleIcon from '@mui/icons-material/SupervisedUserCircle';
import PermIdentityIcon from '@mui/icons-material/PermIdentity';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import ArticleIcon from '@mui/icons-material/Article';
import NoteIcon from '@mui/icons-material/Note';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import BalanceIcon from '@mui/icons-material/Balance';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CategorySalesChart from './CategorySalesChart';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';

function Home() {
    const { isAuthenticated, companyId, username, userId, logout, token, permissions, roleName } = useAuth();
    const [showReports, setShowReports] = useState(false);
    const [favorites, setFavorites] = useState([]);
    const { id } = useParams();

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
        fetchtableData();
    }, [isAuthenticated, token, logout]);

    const hasReadMasterPermission = permissions.includes('Read Master');
    const hasCreateTransactionPermission = permissions.includes('Create Transaction');
    const hasReadTransactionPermission = permissions.includes('Read Transaction');
    const hasCreateAccountsPermission = permissions.includes('Create Accounts');
    const hasReadAccountsPermission = permissions.includes('Read Accounts');
    const hasReadAdministrationPermission = permissions.includes('Read Administration');
    const hasReadReadReportsPermission = permissions.includes('Read Reports');

    const features = [
        { title: "Purchase", icon: <Inventory style={{ color: '#A6CDC6' }} />, link: "/PurchaseCreate" },
        { title: "Billing", icon: <Receipt style={{ color: '#9EBC8A' }} />, link: "/SaleCreate" },
        { title: "Payment", icon: <PaymentIcon style={{ color: '#A0937D' }} />, link: "/PaymentCreate" },
        { title: "Receipt", icon: <ReceiptLongIcon style={{ color: '#80B9AD' }} />, link: "/ReceiptCreate" },
        {
            title: "Reports",
            icon: <Assessment style={{ color: '#657C6A' }} />,
            action: () => setShowReports(!showReports),
        },
    ];

    const reportsList = [
        { id: 1, name: "CashBook", icon: <CurrencyRupeeIcon style={{ color: '#C39898' }} />, link: "/CashBook" },
        { id: 2, name: "Ledger", icon: <SummarizeIcon style={{ color: '#A27B5C' }} />, link: "/Ledger" },
        { id: 3, name: "Creditor Debitor", icon: <SummarizeIcon style={{ color: '#123524' }} />, link: "/CreditorDebitorReport" },
        { id: 4, name: "Customer Outstanding", icon: <PermIdentityIcon style={{ color: '#2C3930' }} />, link: "/CustomerOutstandingReport" },
        { id: 5, name: "Supllier Outstanding", icon: <SupervisedUserCircleIcon style={{ color: '#6D2323' }} />, link: "/SupplierOutstandingReport" },
        { id: 6, name: "Purchase Register", icon: <NoteIcon style={{ color: '#86A788' }} />, link: "/PurchaseRegisterReport" },
        { id: 7, name: "Sale HSNC", icon: <ArticleIcon style={{ color: '#213555' }} />, link: "/SaleHSNCReport" },
        { id: 8, name: "Sale Register", icon: <ArticleIcon style={{ color: '#9AA6B2' }} />, link: "/SaleRegister" },
        { id: 9, name: "Sale Gst", icon: <TextSnippetIcon style={{ color: '#1A4870' }} />, link: "/SaleGstReport" },
        { id: 10, name: "Stock", icon: <ShowChartIcon style={{ color: '#987D9A' }} />, link: "/Stock" },
        { id: 11, name: "Daily Sale Profit", icon: <AttachMoneyIcon style={{ color: '#987D9A' }} />, link: "/DailySaleProfitReport" },
        { id: 12, name: "Trial Balance", icon: <BalanceIcon style={{ color: '#80B9AD' }} />, link: "/TrialBalanceReport" },
    ];

    const toggleFavorite = async (report) => {
        try {
            const existingItems = await fetchData('PinnedItemsApi', `/${companyId}/${userId}`, token);

            if (
                existingItems.response_data?.some(
                    (item) => item.ReportDescription === report.name
                )
            ) {
                alert('This report is already in your favorites.');
                return;
            }

            const pinned = {
                ReportDescription: report.name,
                ReportId: report.id,
                IsLocked: true,
                CompanyId: companyId,
                UserId: userId,
            };

            const response = await postData('PinnedItemsApi', pinned, token);
            fetchtableData(); // Refresh the pinned items
            alert('Favorite added successfully!');
            if (response.status === 200) {
                const updatedFavorites = favorites.includes(report.id)
                    ? favorites.filter((favId) => favId !== report.id)
                    : [...favorites, report.id];
                setFavorites(updatedFavorites);

            } else {
                console.error('Error adding favorite:', response.data?.message || 'Unknown error');
            }
        } catch (error) {
            console.error('Error:', error);
            alert('An error occurred. Please try again.');
        }
    };

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const fetchtableData = async () => {
        fetchData('PinnedItemsApi', `/${companyId}/${userId}`, token)
            .then((response) => {
                if (response.responseCode == '1111') {
                    console.log(response);
                    setData(response.response_data);

                }
            })
            .catch((error) => {
                console.error('Error fetching Supplier Details:', error);
            })
            .finally(() => {
                setLoading(false); // Hide loading screen
            });
    };

    const handleFavoriteRemove = async (id) => {
        try {
            await deleteData('PinnedItemsApi', `${id}`, token); // Perform the delete operation
            fetchtableData(); // Refresh the table data
        } catch (error) {
            console.error('Error deleting record:', error);
        }
    };

    return (
        <div className="container mx-auto p-4">

            <div>
                <Typography variant="h6" className="font-bold justify-end flex items-end" style={{ fontSize: '14px', fontFamily: 'monospace', color: '#1A1A1D', textTransform: 'uppercase' }}>
                    <Diversity1Icon style={{ color: '#547792', fontSize: '14px' }} />
                    <span style={{ fontFamily: 'monospace', fontSize: '12px', marginLeft: '10px', color: '#1A1A1D', textTransform: 'uppercase' }}>
                        Welcome Back, {username || "Guest"}!
                    </span>
                </Typography>
            </div>

            <CategorySalesChart />

            <Grid
                container
                spacing={2}
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'space-evenly',
                    marginTop: '10px',
                }}
            >
                {features.map((feature, index) => {
                    const isAllowed =
                        feature.title === "Payment" || feature.title === "Receipt"
                            ? hasCreateAccountsPermission
                            : feature.title === "Reports"
                                ? hasReadReadReportsPermission
                                : hasCreateTransactionPermission;


                    return (
                        <Grid
                            item
                            xs={12} sm={4} md={2}
                            key={index}
                            sx={{
                                flex: '1 1 auto',
                                maxWidth: '150px',
                            }}
                        >
                            <Card
                                className={`transition ${isAllowed ? 'shadow-lg hover:shadow-2xl' : 'opacity-50 cursor-not-allowed'}`}
                            >
                                <CardContent className="text-center" style={{ height: '100px' }}>
                                    <div className="text-blue-500 mb-2" style={{ marginTop: '-10px' }}>{feature.icon}</div>
                                    <Typography
                                        variant="h6"
                                        className="font-bold"
                                        sx={{
                                            fontSize: '14px',
                                            fontFamily: 'monospace',
                                            color: '#1A1A1D',
                                            textTransform: 'uppercase'
                                        }}
                                    >
                                        {feature.title}
                                    </Typography>

                                    {feature.link ? (
                                        <Button
                                            component={Link}
                                            to={isAllowed ? feature.link : "#"}
                                            disabled={!isAllowed}
                                            className="mt-2"
                                            style={{ backgroundColor: 'none' }}
                                        >
                                            <ArrowCircleRightIcon className="ml-1" style={{ marginTop: '-5px' }} />
                                        </Button>
                                    ) : (
                                        <Button
                                            onClick={isAllowed ? feature.action : undefined}
                                            disabled={!isAllowed}
                                            className="mt-2"
                                        >
                                            View
                                            <ArrowCircleRightIcon className="ml-1" />
                                        </Button>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>
                    );
                })}
            </Grid>


            {showReports && (
                <>
                    <Box
                        className="mt-4 p-4 border rounded shadow-lg"
                        sx={{
                            display: 'grid',
                            gap: 2,
                            gridTemplateColumns: {
                                xs: '1fr',
                                sm: '1fr 1fr',
                                md: 'repeat(3, 1fr)',
                                lg: 'repeat(4, 1fr)',
                            },
                            animation: 'fadeIn 1s ease-in-out',
                            backgroundColor: '#FAF1E6',
                        }}
                        style={{
                            '@keyframes fadeIn': {
                                from: { opacity: 0, transform: 'translateY(10px)' },
                                to: { opacity: 1, transform: 'translateY(0)' },
                            },
                        }}
                    >
                        {reportsList.map((report, index) => (
                            <Card
                                key={index}
                                className="p-4 shadow-lg hover:shadow-2xl transition"
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    color: '#1A1A1D',
                                    textTransform: 'uppercase',
                                    height: '110px',
                                    animation: `cardFadeIn 0.5s ease-in-out ${index * 0.2}s`,
                                }}
                                style={{
                                    '@keyframes cardFadeIn': {
                                        from: { opacity: 0, transform: 'scale(0.9)' },
                                        to: { opacity: 1, transform: 'scale(1)' },
                                    },
                                }}
                            >
                                <CardContent className="text-center">
                                    <div
                                        className="text-blue-500 mb-2"
                                        style={{ marginTop: '-35px' }}
                                    >
                                        {report.icon}
                                    </div>
                                    <Typography
                                        variant="h6"
                                        className="font-bold"
                                        sx={{
                                            fontSize: '14px',
                                            fontFamily: 'monospace',
                                        }}
                                    >
                                        {report.name}
                                    </Typography>
                                    <Button
                                        component={Link}
                                        to={report.link}
                                        variant="outlined"
                                        color="primary"
                                        className="mt-2"
                                    >
                                        Open
                                    </Button>
                                    <IconButton
                                        onClick={() => toggleFavorite(report)}
                                        aria-label={favorites.includes(report.id) ? 'Unfavorite' : 'Favorite'}
                                    >
                                        {favorites.includes(report.id) ? <FavoriteIcon /> : <FavoriteBorderIcon />}
                                    </IconButton>
                                </CardContent>
                            </Card>
                        ))}
                    </Box>
                </>
            )}

            {data.length > 0 && hasReadReadReportsPermission  && (
                <Box className="mt-4 p-4 border rounded shadow-lg">
                    <Typography variant="h6" style={{ fontSize: '14px', marginBottom: '10px', fontFamily: 'monospace', textTransform: 'uppercase', color: 'darkred' }}>Pinned Reports</Typography>
                    <Grid container spacing={2}>
                        {data.map((report, index) => (
                            <Grid item xs={6} sm={5} md={2.9} key={index}>
                                <Card className="p-4 shadow-lg hover:shadow-2xl transition"
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        color: '#410445',
                                        textTransform: 'uppercase',
                                        height: '110px',
                                        animation: `cardFadeIn 0.5s ease-in-out ${index * 0.2}s`,

                                    }}
                                    style={{
                                        '@keyframes cardFadeIn': {
                                            from: { opacity: 0, transform: 'scale(0.9)' },
                                            to: { opacity: 1, transform: 'scale(1)' },
                                        },
                                    }}
                                >
                                    <CardContent className="text-center" style={{ backgroundColor: '' }}>
                                        <div style={{ marginTop: '-35px' }}>{report.icon}</div>
                                        <Typography variant="h6" className="font-bold" sx={{ fontSize: '14px', fontFamily: 'monospace' }}>{report.ReportDescription}</Typography>
                                        <Button
                                            component={Link}
                                            to={
                                                report.ReportDescription === "CashBook"
                                                    ? "/CashBook"
                                                    : report.ReportDescription === "Ledger"
                                                        ? "/Ledger"
                                                        : report.ReportDescription === "Creditor Debitor"
                                                            ? "/CreditorDebitorReport"
                                                            : report.ReportDescription === "Customer Outstanding"
                                                                ? "/CustomerOutstandingReport"
                                                                : report.ReportDescription === "Supllier Outstanding"
                                                                    ? "/SupplierOutstandingReport"
                                                                    : report.ReportDescription === "Purchase Register"
                                                                        ? "/PurchaseRegisterReport"
                                                                        : report.ReportDescription === "Sale HSNC"
                                                                            ? "/SaleHSNCReport"
                                                                            : report.ReportDescription === "SaleRegister"
                                                                                ? "/Sale Register"
                                                                                : report.ReportDescription === "SaleGstReport"
                                                                                    ? "/Sale Gst"
                                                                                    : report.ReportDescription === "Stock"
                                                                                        ? "/Stock"
                                                                                        : report.ReportDescription === "Daily Sale Profit"
                                                                                            ? "/DailySaleProfitReport"
                                                                                            : report.ReportDescription === "Trial Balance"
                                                                                                ? "/TrialBalanceReport"
                                                                                                : report.link
                                            }
                                            variant="outlined"
                                            color="primary"
                                            className="mt-2"
                                        >
                                            Open
                                        </Button>

                                        <IconButton onClick={() => handleFavoriteRemove(report.Id)}>
                                            {/* <FavoriteIcon /> */}
                                            <FavoriteIcon style={{ color: 'red' }} />
                                        </IconButton>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            )}

        </div>
    );
}

export default Home;